<template>
  <b-modal
    :title="isFree ? 'Add to Workforce' : 'Apply Token'"
    centered
    hide-footer
    v-model="show"
    class="pa-4"
    :style="'width: 708px !important;'"
    @hide="onHide"
  >
    <div v-if="invitation">
      <p class="mt-0 mb-3">
        <b>{{ invitation.tradie_name || invitation.name }}</b>
        <!-- Free tier / free trial: the API applies a "free bypass" in
             WorkforceInvitationService::applyToken() (no token consumed), so
             there's nothing token-shaped to show these companies — just the
             plain confirmation. -->
        <template v-if="isFree">
          accepted your workforce invitation. Add them to your active workforce?
        </template>
        <template v-else>
          accepted your workforce invitation. Apply a token to add them to your
          active workforce?
        </template>
      </p>
    </div>

    <template v-if="!isFree">
      <div v-if="balance && balance.available > 0" class="mb-3 mt-2">
        <span :style="tokenColour">
          Available tokens:
          <strong>{{ balance.available }}</strong>
        </span>
      </div>
      <div v-else class="alert alert-warning mb-3 text-center">
        <p class="mb-0">You don't have any available tokens</p>
        <p v-if="canPurchaseTokens" class="mb-0">
          Would you like to purchase tokens?
        </p>
      </div>
    </template>

    <div
      class="token-actions d-flex justify-content-center align-items-stretch pt-2"
    >
      <b-button
        v-if="isFree"
        type="submit"
        variant="primary"
        class="token-btn token-btn--primary"
        @click="onApply"
      >
        ADD TO WORKFORCE
      </b-button>
      <b-button
        v-else-if="balance && balance.available > 0"
        type="submit"
        variant="primary"
        class="token-btn token-btn--primary"
        :disabled="balance.available < 0"
        @click="onApply"
      >
        APPLY TOKEN
      </b-button>
      <b-button
        v-else-if="canPurchaseTokens"
        variant="primary"
        class="token-btn token-btn--primary"
        @click="onPurchaseTokens"
      >
        PURCHASE TOKENS
      </b-button>
      <b-button
        variant="outline-secondary"
        class="token-btn token-btn--cancel"
        @click="isFree ? onClose() : onDismiss()"
      >
        <span class="d-none d-sm-inline">NOT NOW</span>
        <b-icon icon="x" class="d-sm-none" font-scale="1.6" />
      </b-button>
    </div>
  </b-modal>
</template>

<script>
import { canBuyTokens } from '@/utils/tokenAccess'
import { mapGetters } from 'vuex'

// Modeled closely on AssignTokenModal.vue — same prop/emit contract
// (showModal, invitation, emits closeModal/apply/purchaseTokens), same
// balance-threshold coloring, same "0 balance -> offer Purchase Tokens,
// chain into PurchaseTokenModal" pattern already working in
// CompanyTradieList's onAssignModalPurchaseTokens. Like AssignTokenModal,
// this component only emits — the host (wherever it's mounted, e.g.
// NotificationsList.vue) is responsible for dispatching
// candidate/applyToken and candidate/dismissToken.
export default {
  name: 'TokenConfirmationModal',

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    invitation: {
      type: Object,
      default: null,
    },
  },

  computed: {
    ...mapGetters('token', ['balance']),
    ...mapGetters('auth', ['user', 'hasPermission']),

    // Free tier or still inside the 30-day free trial — `is_free` on /auth/me
    // covers both, matching the API's TokenAccessService::companyGrantsFreeAccess().
    // These companies never touch tokens, so this modal drops all token wording
    // and the purchase path and becomes a plain "add to workforce" confirm.
    isFree() {
      return !!(this.user && this.user.is_free)
    },

    canPurchaseTokens() {
      return canBuyTokens(this.user, this.hasPermission)
    },

    show: {
      get() {
        return this.showModal
      },
      set() {
        this.$emit('closeModal')
      },
    },

    tokenColour() {
      if (this.balance && this.balance.available !== undefined) {
        if (this.balance.available <= 3) {
          return {
            backgroundColor: '#dc3545',
            color: '#ffffff',
            padding: '8px 16px',
            borderRadius: '6px',
            display: 'inline-block',
            fontWeight: '500',
          }
        } else if (this.balance.available > 3 && this.balance.available <= 10) {
          return {
            backgroundColor: '#ffc107',
            color: '#000000',
            padding: '8px 16px',
            borderRadius: '6px',
            display: 'inline-block',
            fontWeight: '500',
          }
        }
      }
      return {
        backgroundColor: '#28a745',
        color: '#ffffff',
        padding: '8px 16px',
        borderRadius: '6px',
        display: 'inline-block',
        fontWeight: '500',
      }
    },
  },

  methods: {
    onHide() {
      this.$emit('closeModal')
    },

    onClose() {
      this.$emit('closeModal')
    },

    // Explicit "not now" choice — distinct from onHide (backdrop/escape),
    // which just closes without changing invitation status. This is the
    // D2 dismiss-token action: status stays/becomes token_pending so
    // PendingTokenBanner can surface it for later.
    onDismiss() {
      this.$emit('dismiss')
    },

    onApply() {
      this.$emit('apply')
    },

    onPurchaseTokens() {
      this.$emit('purchaseTokens')
    },
  },
}
</script>

<style lang="scss" scoped>
strong {
  color: inherit;
}

.token-actions {
  gap: 0.5rem;
}
.token-btn {
  font-weight: 600;
}
.token-btn--primary {
  min-width: 132px;
}

@media (max-width: 575.98px) {
  .token-actions {
    gap: 0.35rem;
  }
  .token-btn {
    font-size: 0.8rem;
    padding: 0.4rem 0.6rem;
  }
  .token-btn--primary {
    flex: 1 1 auto;
    min-width: 0;
  }
  .token-btn--cancel {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.4rem 0.6rem;
  }
}
</style>
